import { CATEGORY_ROOT_FILENAME } from "@app/config/const";
import Link from "@components/Atoms/Link";
import type { ReformatArticle, ReformatResult } from "@core/FileStructue/Catalog/reformatCatalog";
import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import { useIsStorageConnected } from "@ext/storage/logic/utils/useStorage";
import { Alert, AlertDescription, AlertIcon, AlertTitle } from "@ui-kit/Alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@ui-kit/AlertDialog";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { Fragment } from "react";

export type ReformatCatalogReportProps = {
	result: ReformatResult;
	onClose: () => void;
	onGoToPublish: () => void;
};

/** The count goes in brackets after the noun, so that no case ending has to agree with it. */
const withCount = (template: string, value: number) => template.replace("{{count}}", String(value));

/** Every category file is `_index.md`, so a category is named by its folder as well. */
const fileName = (path: string) => {
	const parts = path.split("/");
	return parts.at(-1) === CATEGORY_ROOT_FILENAME ? parts.slice(-2).join("/") : parts.at(-1);
};

const Location = ({ article, onNavigate }: { article: ReformatArticle; onNavigate: () => void }) => (
	<span className="text-xs text-muted">
		{article.breadcrumb.map((crumb) => (
			<Fragment key={crumb.pathname}>
				<Link className="text-muted hover:underline" href={{ pathname: crumb.pathname }} onClick={onNavigate}>
					{crumb.title}
				</Link>
				{" / "}
			</Fragment>
		))}
		<span className="whitespace-nowrap">{article.pathname ? fileName(article.path) : article.path}</span>
	</span>
);

// Margin and height are set here because `global.css` gives every `ul li` its own.
const ReportedArticle = ({ article, onNavigate }: { article: ReformatArticle; onNavigate: () => void }) => {
	const title = article.title || article.path;

	return (
		<li className="mb-0 leading-5">
			<div className="flex flex-wrap items-baseline gap-x-2">
				{article.pathname ? (
					<Link
						className="text-[color:var(--color-link)] hover:underline"
						href={{ pathname: article.pathname }}
						onClick={onNavigate}
					>
						{title}
					</Link>
				) : (
					<span className="text-primary-fg">{title}</span>
				)}
				<Location article={article} onNavigate={onNavigate} />
			</div>
		</li>
	);
};

const PREFIX = "forms.catalog-extended-edit-props.props.reformat.report";

type ArticleListProps = { articles: ReformatArticle[]; className?: string; onNavigate: () => void };

const ArticleList = ({ articles, className, onNavigate }: ArticleListProps) => (
	<ul className={cn("list-disc space-y-1 pl-4 marker:text-muted", className)}>
		{articles.map((article) => (
			<ReportedArticle article={article} key={article.path} onNavigate={onNavigate} />
		))}
	</ul>
);

const SkippedArticles = ({ articles, onNavigate }: ArticleListProps) => (
	<Alert focus="high" status="warning">
		<AlertIcon icon="triangle-alert" />
		<AlertTitle>{withCount(t(`${PREFIX}.skipped`), articles.length)}</AlertTitle>
		<AlertDescription>
			<p>{t(`${PREFIX}.skipped-note`)}</p>
			<ScrollShadowContainer className="mt-2 max-h-[20vh]">
				<ArticleList articles={articles} onNavigate={onNavigate} />
			</ScrollShadowContainer>
		</AlertDescription>
	</Alert>
);

const ChangedArticles = ({ articles, count, onNavigate }: ArticleListProps & { count: number }) => (
	<section className="flex flex-col gap-2">
		<p className="font-medium text-primary-fg">{withCount(t(`${PREFIX}.written`), count)}</p>
		<div className="rounded-md border">
			<ScrollShadowContainer className="max-h-[36vh]">
				<ArticleList articles={articles} className="py-2 pl-7 pr-3" onNavigate={onNavigate} />
			</ScrollShadowContainer>
		</div>
	</section>
);

const ReformatCatalogReport = ({ result, onClose, onGoToPublish }: ReformatCatalogReportProps) => {
	const isStorageConnected = useIsStorageConnected();
	const isNothing = result.written === 0 && result.skipped.length === 0;

	return (
		<AlertDialog open>
			<AlertDialogContent status="success">
				{/* Lists read only left-aligned: the ui-kit centres the text below `lg`, and a desktop window is
				    often that narrow -- the same override as `AlertProgressConfirm`. */}
				<AlertDialogHeader className="grid min-w-0 grid-cols-[0_minmax(0,1fr)] items-start gap-y-4">
					<AlertDialogTitle className="col-start-2 mb-0 text-left">{t(`${PREFIX}.title`)}</AlertDialogTitle>
					<AlertDialogDescription asChild className="col-start-2 text-left">
						<div className="flex min-w-0 flex-col gap-4">
							{isNothing && <p>{t(`${PREFIX}.nothing`)}</p>}

							{result.skipped.length > 0 && (
								<SkippedArticles articles={result.skipped} onNavigate={onClose} />
							)}

							{result.changed.length > 0 && (
								<ChangedArticles
									articles={result.changed}
									count={result.written}
									onNavigate={onClose}
								/>
							)}
						</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel onClick={onClose} variant="outline">
						{t("close")}
					</AlertDialogCancel>
					{result.written > 0 && isStorageConnected && (
						<AlertDialogAction onClick={onGoToPublish} status="default" variant="primary">
							{t("forms.catalog-extended-edit-props.props.reformat.publish")}
						</AlertDialogAction>
					)}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

export default ReformatCatalogReport;
